/**
 * CallToAction - Simple CTA section with title, text, and buttons
 * Replaces various action-focused blocks like FreeTrialBlock
 */

import React from 'react'
import { Box, Container } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import TextBlock from '../blocks/TextBlock'
import ButtonBlock from '../blocks/ButtonBlock'

interface CallToActionProps {
	title: string
	description: string
	buttons: Array<{
		text: string
		href?: string
		onClick?: () => void
		style?: 'primary' | 'secondary' | 'outlined'
		openInNewTab?: boolean
	}>
	backgroundColor?: 'primary' | 'secondary' | 'white' | 'transparent'
	textColor?: 'primary' | 'secondary' | 'white' | 'inherit'
	align?: 'left' | 'center' | 'right'
}

const CallToAction: React.FC<CallToActionProps> = ({
	title,
	description,
	buttons,
	backgroundColor = 'primary',
	textColor = 'white',
	align = 'center'
}) => {
	const theme = useTheme()

	const getBackgroundColor = () => {
		switch (backgroundColor) {
			case 'primary':
				return theme.palette.primary.main
			case 'secondary':
				return theme.palette.secondary.main
			case 'white':
				return theme.palette.common.white
			case 'transparent':
				return 'transparent'
			default:
				return theme.palette.primary.main
		}
	}

	return (
		<Box
			component="section"
			sx={{
				backgroundColor: getBackgroundColor(),
				py: { md: 8, xs: 4 }
			}}
		>
			<Container>
				<Box
					sx={{
						textAlign: align,
						maxWidth: { xs: '100%', md: '800px' },
						mx: align === 'center' ? 'auto' : undefined
					}}
				>
					<TextBlock
						title={title}
						titleLevel={2}
						titleColor={textColor}
						content={description}
						contentColor={textColor}
						align={align}
						spacing="normal"
					/>

					<Box sx={{ mt: 4 }}>
						<ButtonBlock buttons={buttons} layout="horizontal" align={align} size="large" spacing="normal" />
					</Box>
				</Box>
			</Container>
		</Box>
	)
}

export default CallToAction
